import { Component, OnInit, Input, ViewEncapsulation, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { IdService } from '../../../shared/services/id.service';
import { Locale } from '../datatable.models';
import { IDatatablePaginatorEvent, IDatatablePageSizeEvent } from './datatable-paginator.types';
import { IGenericSelectOption } from '../../../shared/types/forms';
import { FormGroup, FormControl, AbstractControl } from '@angular/forms';

@Component({
	selector: 'rbc-datatable-paginator',
	templateUrl: './datatable-paginator.component.html',
	styleUrls: ['./datatable-paginator.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class DatatablePaginatorComponent implements OnInit {
	@Input() id: string;
	@Input() totalPages: number;
	@Input()
	set paginationDisplay(displayNumbers: number[]) {
		this._paginationDisplay = displayNumbers ? displayNumbers : [5, 10, 30, 50];
	}
	get paginationDisplay() {
		return this._paginationDisplay;
	}
	@Input()
	set activePage(activePage: number) {
		this._activePage = activePage > 0 ? activePage : 1;
	}
	get activePage() {
		return this._activePage;
	}
	@Input() set paginationDisplayPreselected(pageDisplaySize: string) {
		this._paginationDisplayPreselected = pageDisplaySize || this._defaultPaginationDisplayPreselected;
		this.pageSize.patchValue(this._paginationDisplayPreselected);
	}
	get paginationDisplayPreselected() {
		return this._paginationDisplayPreselected;
	}
	@Input() locale: Locale;

	@Output('onpaginatorbuttonclicked') onPaginatorButtonClicked = new EventEmitter<IDatatablePaginatorEvent>();
	@Output('onpagesizeselectchanged') onPageSizeSelectChanged = new EventEmitter<IDatatablePageSizeEvent>();

	displayOptions: IGenericSelectOption[];
	currentPage: number;
	private _activePage: number = 1;
	private _paginationDisplay: number[];
	private _defaultPaginationDisplayPreselected: string = '5';
	private _paginationDisplayPreselected: string = this._defaultPaginationDisplayPreselected;

	get pageDisplay(): number[] {
		// set the max page numbers displayed between the first and last page numbers
		const maxDisplay = 5;
		let pageArr = [];

		// check if the number of pages goes beyond the max displayed pages (including the first and last page)
		if (this.totalPages <= maxDisplay + 2) {
			for (let i = 1; i <= this.totalPages; i++) {
				pageArr.push(i);
			}

			return pageArr;

			// if there are more pages than the max display, then add a page mask
		} else {
			// if displaying one page mask at the end of the paginator
			if (this.currentPage <= maxDisplay - 1) {
				for (let i = 1; i < maxDisplay; i++) {
					pageArr.push(i + 1);
				}
				pageArr = [...pageArr, null];

				// if displaying one page mask at the front of the paginator
			} else if (this.currentPage > this.totalPages - maxDisplay + 1) {
				const endPage = this.totalPages - maxDisplay;
				for (let i = 1; i < maxDisplay; i++) {
					pageArr.push(endPage + i);
				}
				pageArr = [null, ...pageArr];

				// if we need 2 page masks
			} else {
				const middlePages = [];
				const middle = Math.round((maxDisplay - 2) / 2);

				// loop though the middle pages (minus the page masks)
				// to determine the pages to be placed before and after the middle page
				for (let i = 1; i <= maxDisplay - 2; i++) {
					if (i < middle) {
						middlePages.push(this.currentPage - (middle - i));
					} else if (i > middle) {
						middlePages.push(this.currentPage + (i - middle));
					} else {
						middlePages.push(this.currentPage);
					}
				}

				pageArr = [null, ...middlePages, null];
			}

			// add the generated pages between the first and total pages
			return [1, ...pageArr, this.totalPages];
		}
	}

	get pageSize(): AbstractControl {
		return this.form.get('pageSize');
	}

	form: FormGroup;

	constructor(private idService: IdService) {
		this.form = new FormGroup({
			pageSize: new FormControl(this.paginationDisplayPreselected)
		});
	}

	ngOnInit() {
		if (!this.id) {
			this.id = this.idService.generate();
		}
		if (this.activePage && this.activePage > 0 && this.activePage <= this.totalPages) {
			this.currentPage = this.activePage;
		}
		this.displayOptions = this.getDisplayOptions();
	}

	getDisplayOptions(): IGenericSelectOption[] {
		return this.paginationDisplay
			? this.paginationDisplay.map((n, i) => {
					return {
						label: n + '',
						value: n + '',
						selected: i == (this.paginationDisplayPreselected || 0)
					};
			  })
			: null;
	}

	onFirstPageButtonClicked(clickEvent?: Event) {
		this.currentPage = 1;
		this.onPaginatorButtonClicked.emit({ pageNumber: 1 });
	}
	onPreviousPageButtonClicked(clickEvent?: Event) {
		// If current page is not the first page, currentPage--
		if (this.currentPage > 1) {
			this.currentPage--;
		}
		this.onPaginatorButtonClicked.emit({ pageNumber: this.currentPage });
	}
	onNextPageButtonClicked(clickEvent?: Event) {
		// If current page is not the last page, currentPage++
		if (this.currentPage < this.totalPages) {
			this.currentPage++;
		}
		this.onPaginatorButtonClicked.emit({ pageNumber: this.currentPage });
	}
	onPageNumberButtonClicked(pageNumber: number) {
		// Reset current page based on the clicked button's number
		if (pageNumber) {
			this.currentPage = pageNumber;
		}
		this.onPaginatorButtonClicked.emit({ pageNumber: this.currentPage });
	}
	onLastPageButtonClicked(event: Event) {
		this.currentPage = this.totalPages;
		this.onPaginatorButtonClicked.emit({ pageNumber: this.totalPages });
	}
	onPageSizeSelectChange(selectEvent: IGenericSelectOption) {
		// Reset current page to the first page
		this.currentPage = 1;
		this.onPageSizeSelectChanged.emit({
			currentPage: this.currentPage,
			pageSize: parseInt(selectEvent.value, 10)
		});
	}
}
